/*
  Logo animation controller — vanilla JS, no GSAP.

  Toggles two classes on `.logo-animated`:
    • `.is-revealing` — applied on page load on the homepage. CSS
                         @keyframes in logo-animation.css runs the
                         reveal animation (H/OME slide down, B/OUND
                         slide up).
    • `.is-monogram`  — applied when the header turns opaque
                         (`.header--opaque` class on the sticky
                         header). CSS transitions in
                         logo-animation.css morph the logo into an
                         H-over-B monogram (OME + OUND fade out, H
                         rises, B translates to under H).

  All H + OME + B + OUND transform values are integer pixels declared
  statically in logo-animation.css so the SVG render stays crisp across
  devices.
*/

(function () {
  'use strict';

  function initLogoAnimation() {
    var container = document.querySelector('.logo-animated');
    if (!container) return;

    var partH = container.querySelector('.logo-animated__h');
    var partOme = container.querySelector('.logo-animated__ome');
    var partB = container.querySelector('.logo-animated__b');
    var partOund = container.querySelector('.logo-animated__ound');
    var partMonogram = container.querySelector('.logo-animated__monogram');
    var headerEl = document.querySelector('.shopify-section-header');
    var headerWrapper = document.querySelector('.header-wrapper');
    var isHomepage = window.location.pathname === '/' || !!document.querySelector('body.template-index');
    // The reveal plays on the homepage only.
    var shouldPlayReveal = isHomepage;

    if (!partH || !partOme || !partB || !partOund) return;

    // ===== Monogram state — watch header for `.header--opaque` =====
    var isMonogram = false;
    var monogramReady = false;
    var observerAttached = false;
    var revealTimer = null;

    function checkOpaqueState() {
      if (!headerEl || !monogramReady) return;
      var overFooter = headerWrapper && headerWrapper.classList.contains('header--over-footer');
      // The v1 header signals its scrolled/solid state with `.header--opaque`
      // on the section element. Header v2 instead REMOVES `.header--at-top`
      // from its wrapper (it deliberately never sets `.header--opaque`, which
      // base.css would use to repaint the bar its own colour). Detect v2 via
      // `.header-v2-wrapper` — a class the live v1 header never carries, so the
      // v1 branch below is unchanged — and read its at-top flag instead.
      var isV2 = headerWrapper && headerWrapper.classList.contains('header-v2-wrapper');
      var opaque = isV2
        ? !headerWrapper.classList.contains('header--at-top')
        : headerEl.classList.contains('header--opaque');
      var shouldBeMonogram = opaque && !overFooter;

      if (shouldBeMonogram && !isMonogram) {
        isMonogram = true;
        container.classList.add('is-monogram');
      } else if (!shouldBeMonogram && isMonogram) {
        isMonogram = false;
        container.classList.remove('is-monogram');
      }
    }

    function attachMonogramObserver() {
      // Idempotent — observer is set up once for the lifetime of the
      // page (including bfcache restores). Gating happens via
      // `monogramReady` so we can disarm during a reveal replay.
      if (observerAttached || !headerEl) return;
      observerAttached = true;

      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].attributeName === 'class') {
            checkOpaqueState();
            break;
          }
        }
      });

      observer.observe(headerEl, { attributes: true, attributeFilter: ['class'] });
      if (headerWrapper) {
        observer.observe(headerWrapper, { attributes: true, attributeFilter: ['class'] });
      }
    }

    function armMonogramWatch() {
      monogramReady = true;
      attachMonogramObserver();
      checkOpaqueState();
    }

    function playReveal() {
      // Cancel any pending reveal teardown (defensive — only relevant
      // when this fires on a bfcache restore while the prior reveal's
      // 1500ms timer is still in flight).
      if (revealTimer) {
        clearTimeout(revealTimer);
        revealTimer = null;
      }

      // Disarm monogram watch FIRST so the observer can't fire and
      // add `.is-monogram` mid-reveal. Bug symptom otherwise: keyframe
      // `animation-fill-mode: both` keeps wordmark at opacity 1 while
      // `.is-monogram` separately makes the monogram visible → both
      // show stacked, looks like the wordmark "won't disappear".
      monogramReady = false;
      isMonogram = false;
      container.classList.remove('is-monogram');

      // Defer adding the class by one frame so the visibility flip
      // and the keyframe FROM state both apply on the same paint
      // (no FOUC where elements render at default state first).
      requestAnimationFrame(function () {
        container.classList.add('is-revealing');
      });

      // Reveal animation = 0.2s delay + 1.2s duration = 1.4s total.
      // After it ends we REMOVE `.is-revealing` because the @keyframes
      // animation (with `animation-fill-mode: both`) keeps overriding
      // the transform via the keyframe TO state even after the
      // animation ends. That blocks `.is-monogram` from taking effect
      // (animations win over cascade). Once `.is-revealing` is off,
      // cascade reasserts and the monogram transitions can fire.
      // Timing 1500ms: 100ms buffer past the animation's 1400ms end.
      revealTimer = setTimeout(function () {
        revealTimer = null;
        container.classList.remove('is-revealing');
        // Force a synchronous reflow so the cascade-default state is
        // computed and committed BEFORE `.is-monogram` is applied.
        // Then a rAF for good measure. Without the offsetHeight read,
        // the browser bundles the class remove + class add into a
        // single style update — the transition has no rendered 'from'
        // state to animate from and the logo snaps to monogram instead
        // of morphing. Visible specifically when the user lands on a
        // page already scrolled past the hero (header opaque from t=0).
        void container.offsetHeight;
        requestAnimationFrame(armMonogramWatch);
      }, 1500);
    }

    function skipRevealAndArm() {
      // Non-reveal pages: visibility just flipped to visible. Force a
      // synchronous reflow + rAF before arming the monogram watch so
      // the wordmark default state renders before `.is-monogram` is
      // applied (same snap-without-transition issue as above).
      void container.offsetHeight;
      requestAnimationFrame(armMonogramWatch);
    }

    // Replay reveal on bfcache restore (back/forward navigation).
    // Inside initLogoAnimation so it can share state with the rest
    // of the controller — critical: the old handler at IIFE scope
    // couldn't reset `monogramReady` / `isMonogram` in the closure,
    // so the stale observer kept firing during the replayed reveal
    // and added `.is-monogram` over the still-revealing wordmark.
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      if (shouldPlayReveal) {
        playReveal();
      } else {
        // Header state may have changed during the navigation away;
        // re-evaluate immediately.
        armMonogramWatch();
      }
    });

    // ===== Kick off =====
    container.style.visibility = 'visible';

    if (shouldPlayReveal) {
      playReveal();
    } else {
      skipRevealAndArm();
    }
  }

  function ready() {
    initLogoAnimation();
  }

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    ready();
  } else {
    window.addEventListener('load', ready);
  }
})();
